import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { 
  Heart, HeartCrack, Users, MessageCircle, Sparkles, RefreshCw,
  Phone, Video, Calendar, Clock, Star, ChevronRight, ChevronLeft, X,
  CheckCircle, ArrowRight, Quote, CreditCard, Shield, PenSquare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const counselingServices = [
  {
    id: "marriage",
    icon: Users,
    title: "결혼과 재혼",
    subtitle: "결혼과 기존",
    description: "이 사람과 결혼하는 게 정말 맞는 걸까? 사랑하지만, 왠지 마음에 확신이 없다. 행복의 시작일까, 또 한 번의 상처일까.",
    color: "from-stone-500 to-stone-600",
    bgColor: "bg-stone-50",
    features: ["결혼 준비 상담", "재혼 가정 상담", "가족 관계 조율", "웨딩 플래닝 조언"],
    price: "USD $100",
    duration: "1시간",
  },
  {
    id: "love",
    icon: Heart,
    title: "연애와 사랑",
    subtitle: "연애와 사랑",
    description: "사랑이란 늘 설레지만, 언젠가부터 두려워졌습니다. 이 사람은 나를 진심으로 사랑하는 것일까. 혹은 내가 그를 진심으로 사랑할 수 있을까. 반복되는 패턴 속에서 나는 오늘도 마음의 문 앞에서 망설입니다.",
    color: "from-stone-400 to-stone-500",
    bgColor: "bg-stone-50",
    features: ["연애 시작 코칭", "관계 발전 상담", "데이트 스킬업", "소통 방법 개선"],
    price: "USD $100",
    duration: "1시간",
  },
  {
    id: "breakup",
    icon: HeartCrack,
    title: "이별과 이혼",
    subtitle: "이별과 이혼",
    description: "헤어졌는데도, 여전히 그 사람이 떠오를 때가 있습니다. 감정은 그 자리에 머물러 있죠. 잊고 싶은데 잊지 못할 기억들 사이에서 나는 오늘도 나를 완전히 찾지 못하고 있습니다.",
    color: "from-stone-600 to-stone-700",
    bgColor: "bg-stone-50",
    features: ["이별 극복 상담", "이혼 과정 지원", "감정 회복 프로그램", "새 출발 코칭"],
    price: "USD $100",
    duration: "1시간",
  },
  {
    id: "reunion",
    icon: RefreshCw,
    title: "재회상담",
    subtitle: "재회상담",
    description: "시간이 흘렀는데도 그 사람이 자꾸 생각납니다. 그다음이 후회인지, 아니면 외로움인지 구분이 안 될 때가 있습니다. 다시 만나면 달라질까? 그때 하지 못한 말들을 지금도 붙잡고 있습니다.",
    color: "from-stone-500 to-stone-600",
    bgColor: "bg-stone-50",
    features: ["재회 가능성 분석", "관계 복원 전략", "변화 포인트 코칭", "재발 방지 상담"],
    price: "USD $100",
    duration: "1시간",
  },
  {
    id: "couple",
    icon: Sparkles,
    title: "커플 테라피",
    subtitle: "커플테라피",
    description: "사랑하지만 서로의 말이 자꾸 어긋납니다. 아무 말도 하지 않아도 알 줄 알았는데, 이제는 말하지 않으면 아무것도 전해지지 않는 걸 알게 됩니다. 마음은 여전히 곁에 있는데, 다가갈수록 멀어지는 느낌.",
    color: "from-stone-400 to-stone-500",
    bgColor: "bg-stone-50",
    features: ["커플 소통 훈련", "갈등 해결 워크숍", "친밀감 회복", "미래 설계 상담"],
    price: "USD $100",
    duration: "1시간",
  },
  {
    id: "other",
    icon: MessageCircle,
    title: "기타상담",
    subtitle: "기타상담",
    description: "누군가를 만나도 늘 비슷한 상처를 반복할 때, 문제는 나일까 관계일까 혼란스러워졌습니다. 괜찮은 척하지만, 웃는 게 점점 어려워졌습니다.",
    color: "from-stone-600 to-stone-700",
    bgColor: "bg-stone-50",
    features: ["자유 주제 상담", "라이프 코칭", "자존감 향상", "스트레스 관리"],
    price: "USD $100",
    duration: "1시간",
  },
];

const counselors = [
  {
    name: "손리즈",
    title: "대표 상담사",
    specialty: "결혼/재혼 전문",
    experience: "20년+",
    image: "https://customer-assets.emergentagent.com/job_spreadsheet-portal-4/artifacts/qe5s1b3f_1727021728262.jpg",
    rating: 4.9,
    reviews: 328,
  },
  {
    name: "김연희",
    title: "수석 상담사",
    specialty: "연애/이별 전문",
    experience: "15년+",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400",
    rating: 4.8,
    reviews: 256,
  },
  {
    name: "박준혁",
    title: "전문 상담사",
    specialty: "커플 테라피",
    experience: "12년+",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400",
    rating: 4.7,
    reviews: 189,
  },
];

const testimonials = [
  {
    content: "재혼 상담을 통해 새로운 가정을 꾸릴 수 있었어요. 손리즈 선생님께 정말 감사드립니다.",
    author: "김○○ (52세)",
    service: "결혼과 재혼",
  },
  {
    content: "이별 후 힘든 시간을 보내다가 상담을 받았는데, 마음의 짐을 내려놓을 수 있었습니다.",
    author: "이○○ (47세)",
    service: "이별과 이혼",
  },
  {
    content: "커플 테라피 덕분에 남편과의 소통이 훨씬 좋아졌어요. 강력 추천합니다!",
    author: "박○○ (55세)",
    service: "커플 테라피",
  },
];

const defaultTestimonials = [
  {
    content: "재혼 상담을 통해 새로운 가정을 꾸릴 수 있었어요. 손리즈 선생님께 정말 감사드립니다.",
    author: "김○○ (52세)",
    service: "결혼과 재혼",
    rating: 5,
  },
  {
    content: "이별 후 힘든 시간을 보내다가 상담을 받았는데, 마음의 짐을 내려놓을 수 있었습니다.",
    author: "이○○ (47세)",
    service: "이별과 이혼",
    rating: 5,
  },
  {
    content: "커플 테라피 덕분에 남편과의 소통이 훨씬 좋아졌어요. 강력 추천합니다!",
    author: "박○○ (55세)",
    service: "커플 테라피",
    rating: 5,
  },
];

export default function CounselingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [selectedService, setSelectedService] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingForm, setBookingForm] = useState({ serviceId: "", method: "", phone: "", kakaoId: "", content: "" });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, service: "", content: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [showReviewCompleteModal, setShowReviewCompleteModal] = useState(false);
  const [reviewPage, setReviewPage] = useState(0);
  const [allTestimonials, setAllTestimonials] = useState(defaultTestimonials);

  // Fetch reviews from API
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch(`${API_URL}/api/counseling-reviews?limit=50`);
        if (res.ok) {
          const data = await res.json();
          if (data.reviews && data.reviews.length > 0) {
            const dbReviews = data.reviews.map(r => ({
              content: r.content,
              author: r.author,
              service: r.service,
              rating: r.rating || 5,
            }));
            setAllTestimonials([...dbReviews, ...defaultTestimonials]);
          }
        }
      } catch { /* use defaults */ }
    };
    fetchReviews();
  }, []);

  // Handle payment return from Stripe
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      setShowCompleteModal(true);
    }
  }, [searchParams]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_a9274a80-d9c6-42bd-a282-d2ba907c1b71/artifacts/3t8obbcb_ChatGPT%20Image%20Apr%201%2C%202026%2C%2006_31_53%20PM.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-gold-300 text-sm font-medium mb-6">
              Professional Counseling
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-[720] text-white mb-6">
              사람과 관계속에서
              <br />
              <span className="text-gold-400">길을 잃었을 때</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
              리즈가 나침판이 되어드립니다
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={() => setShowBookingModal(true)}
                className="px-8 py-6 bg-gold-500 hover:bg-gold-600 text-stone-900 font-semibold rounded-full shadow-lg shadow-gold-500/30"
                data-testid="book-consultation-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                상담 예약하기
              </Button>
              <Button
                variant="outline"
                className="px-8 py-6 border-2 border-white/30 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                카톡으로 문의하기
              </Button>
            </div>
          </motion.div>
          
          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 grid grid-cols-3 gap-8 max-w-3xl mx-auto"
          >
            {[
              { value: "7+", label: "년 경력" },
              { value: "1,000+", label: "상담 건수" },
              { value: "90%", label: "만족도" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-gold-400">{stat.value}</p>
                <p className="text-white/60 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Consultation Methods */}
      <section className="py-12 bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {[
              { icon: Video, label: "화상 상담", desc: "편안한 공간에서" },
              { icon: Phone, label: "전화 상담", desc: "언제 어디서든" },
              { icon: MessageCircle, label: "채팅 상담", desc: "부담 없이 시작" },
              { icon: Users, label: "대면 상담", desc: "직접 만나서" },
            ].map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 text-stone-600"
              >
                <div className="w-12 h-12 bg-burgundy-50 rounded-xl flex items-center justify-center">
                  <method.icon className="w-6 h-6 text-burgundy-500" />
                </div>
                <div>
                  <p className="font-semibold text-stone-800">{method.label}</p>
                  <p className="text-sm text-stone-500">{method.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20" data-testid="counseling-services-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2 mb-4">
              상담 서비스
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              다양한 분야의 전문 상담을 통해 당신의 고민을 해결해 드립니다
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {counselingServices.map((service) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="group bg-white rounded-2xl border border-stone-200 hover:border-burgundy-300 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
                  data-testid={`service-card-${service.id}`}
                >
                  {/* Card Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-burgundy-50 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-burgundy-500" />
                      </div>
                      <h3 className="text-xl font-bold text-burgundy-600">{service.title}</h3>
                    </div>
                    <p className="text-stone-500 text-sm mb-5 leading-relaxed">{service.description}</p>
                    
                    {/* Features with checkmarks */}
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">포함 서비스</p>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-burgundy-50 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-burgundy-500" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span className="text-sm text-stone-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Price & Book */}
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <div>
                        <p className="text-xl font-bold text-stone-800">{service.price}</p>
                        <p className="text-xs text-stone-400">{service.duration} 기준</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-stone-800 hover:bg-stone-900 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-all group-hover:bg-burgundy-600"
                        onClick={() => { setSelectedService(service); setShowBookingModal(true); }}
                      >
                        상담 예약하기
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Counselors Section - Hidden */}
      {/* 
      <section className="py-20 bg-stone-100">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">Our Team</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2 mb-4">
              전문 상담사
            </h2>
            <p className="text-stone-600">
              풍부한 경험과 전문성을 갖춘 상담사가 함께합니다
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {counselors.map((counselor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-burgundy-100">
                  <img
                    src={counselor.image}
                    alt={counselor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-stone-800">{counselor.name}</h3>
                <p className="text-burgundy-500 text-sm font-medium">{counselor.title}</p>
                <p className="text-stone-600 text-sm mt-2">{counselor.specialty}</p>
                <p className="text-stone-500 text-xs mt-1">경력 {counselor.experience}</p>
                
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
                  <span className="font-semibold text-stone-800">{counselor.rating}</span>
                  <span className="text-stone-500 text-sm">({counselor.reviews} 리뷰)</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Testimonials */}
      <section className="py-20 bg-stone-500">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-stone-300 font-medium text-sm tracking-wider uppercase">Reviews</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-white mt-2">
              상담 후기
            </h2>
          </motion.div>

          <div className="relative">
            {/* Left arrow */}
            <button
              onClick={() => setReviewPage(Math.max(0, reviewPage - 1))}
              className={`absolute -left-16 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors ${reviewPage === 0 ? "opacity-20 cursor-default" : ""}`}
              disabled={reviewPage === 0}
              data-testid="review-prev"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>

            {/* Right arrow */}
            <button
              onClick={() => setReviewPage(Math.min(Math.ceil(allTestimonials.length / 3) - 1, reviewPage + 1))}
              className={`absolute -right-16 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors ${reviewPage >= Math.ceil(allTestimonials.length / 3) - 1 ? "opacity-20 cursor-default" : ""}`}
              disabled={reviewPage >= Math.ceil(allTestimonials.length / 3) - 1}
              data-testid="review-next"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>

            <div className="overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-transform duration-300" style={{ transform: `translateX(-${reviewPage * 100}%)` }}>
                {allTestimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 min-w-full md:min-w-0"
                  >
                    <Quote className="w-8 h-8 text-gold-400 mb-4" />
                    <p className="text-white/90 mb-4">{testimonial.content}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{testimonial.author}</p>
                        <p className="text-white/60 text-sm">{testimonial.service}</p>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-gold-400 fill-gold-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {[...Array(Math.max(1, Math.ceil(allTestimonials.length / 3)))].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setReviewPage(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === reviewPage ? "bg-white w-6" : "bg-white/40"}`}
                />
              ))}
            </div>
          </div>

          {/* 후기 남기기 버튼 */}
          <div className="text-center mt-10">
            <Button
              onClick={() => setShowReviewModal(true)}
              className="px-8 py-4 bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-full font-medium backdrop-blur-sm transition-all"
            >
              <PenSquare className="w-4 h-4 mr-2" />
              후기 남기기
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - Hidden */}
      {/*
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mb-4">
              지금 바로 상담을 시작하세요
            </h2>
            <p className="text-stone-600 mb-8">
              첫 상담 30분 무료 체험으로 부담 없이 시작해 보세요
            </p>
            <Button
              onClick={() => setShowBookingModal(true)}
              className="px-10 py-6 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-semibold text-lg rounded-full shadow-lg shadow-burgundy-500/30"
            >
              무료 상담 신청하기
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
      */}

      <FooterSection />

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => { setShowBookingModal(false); setBookingStep(1); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Step indicator */}
              <div className="bg-gradient-to-r from-stone-600 to-stone-400 p-3 text-white relative">
                <button
                  onClick={() => { setShowBookingModal(false); setBookingStep(1); }}
                  className="absolute top-2 right-2 p-1.5 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <Calendar className="w-6 h-6 mb-1" />
                <h3 className="text-lg font-bold">{bookingStep === 1 ? "상담 예약" : "결제하기"}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-6 h-1 rounded-full ${bookingStep >= 1 ? "bg-white" : "bg-white/30"}`} />
                  <div className={`w-6 h-1 rounded-full ${bookingStep >= 2 ? "bg-white" : "bg-white/30"}`} />
                  <span className="text-white/70 text-[10px] ml-1">{bookingStep}/2</span>
                </div>
              </div>

              {bookingStep === 1 && (
                <div className="p-4">
                  <div className="space-y-2.5">
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">상담 유형</label>
                      <select
                        value={bookingForm.serviceId || selectedService?.id || ""}
                        onChange={(e) => setBookingForm({...bookingForm, serviceId: e.target.value})}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                      >
                        <option value="">상담 유형 선택</option>
                        {counselingServices.map((service) => (
                          <option key={service.id} value={service.id}>{service.title}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">상담 방식</label>
                      <div className="grid grid-cols-2 gap-2">
                        {["화상 상담", "전화 상담"].map((method) => (
                          <label key={method} className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${bookingForm.method === method ? "border-burgundy-400 bg-burgundy-50" : "border-stone-200 hover:border-burgundy-300"}`}>
                            <input
                              type="radio"
                              name="method"
                              checked={bookingForm.method === method}
                              onChange={() => setBookingForm({...bookingForm, method})}
                              className="text-burgundy-500"
                            />
                            <span className="text-sm text-stone-700">{method}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">전화번호 <span className="text-burgundy-500">*</span></label>
                      <input
                        type="tel"
                        placeholder="010-0000-0000"
                        value={bookingForm.phone}
                        onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">카톡 아이디</label>
                      <input
                        type="text"
                        placeholder="카카오톡 아이디"
                        value={bookingForm.kakaoId}
                        onChange={(e) => setBookingForm({...bookingForm, kakaoId: e.target.value})}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">간단한 상담 내용</label>
                      <textarea
                        rows={2}
                        placeholder="상담받고 싶은 내용을 간단히 적어주세요"
                        value={bookingForm.content}
                        onChange={(e) => setBookingForm({...bookingForm, content: e.target.value})}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500 resize-none"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={() => {
                      if (!bookingForm.phone) return alert("전화번호를 입력해주세요.");
                      if (!bookingForm.serviceId && !selectedService) return alert("상담 유형을 선택해주세요.");
                      setBookingStep(2);
                    }}
                    className="w-full mt-3 py-4 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-semibold rounded-xl"
                  >
                    결제하기
                  </Button>
                  
                  <p className="text-center text-burgundy-600 font-medium text-xs mt-2 pb-1">
                    * 상담일자와 시간은 개별적으로 조율합니다
                  </p>
                </div>
              )}

              {bookingStep === 2 && (() => {
                const svc = counselingServices.find(s => s.id === (bookingForm.serviceId || selectedService?.id)) || counselingServices[0];
                return (
                  <div className="p-4">
                    {/* Order summary */}
                    <div className="bg-stone-50 rounded-xl p-4 mb-4">
                      <h4 className="text-xs font-medium text-stone-500 mb-2">주문 내역</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-600">{svc.title}</span>
                          <span className="font-bold text-stone-800">{svc.price}</span>
                        </div>
                        <div className="flex justify-between text-xs text-stone-400">
                          <span>{svc.duration} 기준</span>
                          <span>{bookingForm.method || "상담 방식 미선택"}</span>
                        </div>
                        <div className="border-t border-stone-200 pt-2 mt-2 flex justify-between">
                          <span className="text-sm font-bold text-stone-700">총 결제 금액</span>
                          <span className="text-lg font-bold text-burgundy-600">{svc.price}</span>
                        </div>
                      </div>
                    </div>

                    {/* Contact info summary */}
                    <div className="bg-stone-50 rounded-xl p-4 mb-4">
                      <h4 className="text-xs font-medium text-stone-500 mb-2">연락처 정보</h4>
                      <div className="space-y-1 text-sm text-stone-600">
                        <p>전화번호: {bookingForm.phone}</p>
                        {bookingForm.kakaoId && <p>카톡 ID: {bookingForm.kakaoId}</p>}
                      </div>
                    </div>

                    {/* Payment method */}
                    <div className="bg-stone-50 rounded-xl p-4 mb-4">
                      <h4 className="text-xs font-medium text-stone-500 mb-2">결제 수단</h4>
                      <div className="flex items-center gap-3 p-3 border border-burgundy-300 bg-white rounded-lg">
                        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-800 text-sm">Stripe 보안 결제</p>
                          <p className="text-[11px] text-stone-500">신용카드 / 체크카드</p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-burgundy-500 ml-auto" />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setBookingStep(1)}
                        className="flex-1 py-4 rounded-xl"
                      >
                        이전
                      </Button>
                      <Button
                        disabled={isProcessing}
                        onClick={async () => {
                          if (!isAuthenticated) {
                            alert("로그인이 필요합니다.");
                            return;
                          }
                          setIsProcessing(true);
                          try {
                            const authToken = localStorage.getItem("liztv_token");
                            const res = await fetch(`${API_URL}/api/counseling/book`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
                              body: JSON.stringify({
                                service_id: bookingForm.serviceId || selectedService?.id || svc.id,
                                method: bookingForm.method,
                                phone: bookingForm.phone,
                                kakao_id: bookingForm.kakaoId,
                                content: bookingForm.content,
                                origin_url: window.location.origin,
                              }),
                            });
                            const data = await res.json();
                            if (res.ok && data.url) {
                              window.location.href = data.url;
                            } else {
                              alert(data.detail || "결제 처리 중 오류가 발생했습니다. 로그인 상태를 확인해주세요.");
                            }
                          } catch (err) {
                            alert("결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
                          }
                          setIsProcessing(false);
                        }}
                        className="flex-[2] py-4 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-semibold rounded-xl"
                      >
                        {isProcessing ? "처리 중..." : `${svc.price} 결제하기`}
                      </Button>
                    </div>

                    <div className="flex items-center justify-center gap-1 mt-3 pb-1">
                      <Shield className="w-3 h-3 text-stone-400" />
                      <span className="text-[11px] text-stone-400">SSL 암호화로 안전하게 결제됩니다</span>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Write Modal */}
      <AnimatePresence>
        {showReviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowReviewModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="review-modal"
            >
              <div className="bg-gradient-to-r from-stone-600 to-stone-400 p-4 text-white relative rounded-t-3xl">
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <PenSquare className="w-6 h-6 mb-1" />
                <h3 className="text-lg font-bold">상담 후기 남기기</h3>
                <p className="text-white/70 text-[11px]">소중한 후기를 남겨주세요</p>
              </div>

              <div className="p-5 space-y-4">
                {/* Star Rating */}
                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-2">만족도</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setReviewForm({...reviewForm, rating: star})}
                        className="transition-transform hover:scale-110"
                        data-testid={`star-${star}`}
                      >
                        <Star className={`w-8 h-8 ${star <= reviewForm.rating ? "text-gold-400 fill-gold-400" : "text-stone-200"}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-1">상담 유형</label>
                  <select
                    value={reviewForm.service}
                    onChange={(e) => setReviewForm({...reviewForm, service: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                  >
                    <option value="">상담 유형 선택</option>
                    {counselingServices.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                </div>

                {/* Review Content */}
                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-1">후기 내용</label>
                  <textarea
                    value={reviewForm.content}
                    onChange={(e) => setReviewForm({...reviewForm, content: e.target.value})}
                    rows={5}
                    placeholder="상담 경험을 자유롭게 작성해주세요"
                    className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 resize-none"
                    data-testid="review-content"
                  />
                </div>

                {/* Submit */}
                <Button
                  disabled={!reviewForm.content.trim() || reviewSubmitting}
                  onClick={async () => {
                    if (!isAuthenticated) return alert("로그인이 필요합니다.");
                    setReviewSubmitting(true);
                    const name = user?.name || user?.username || "익명";
                    const maskedName = name.length > 1 ? name[0] + "○".repeat(name.length - 1) : name + "○○";
                    const age = user?.birth_year ? new Date().getFullYear() - user.birth_year : null;
                    const authorLabel = age ? `${maskedName} (${age}세)` : maskedName;
                    try {
                      const authToken = localStorage.getItem("liztv_token");
                      const res = await fetch(`${API_URL}/api/counseling-reviews`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
                        body: JSON.stringify({ ...reviewForm, author: authorLabel }),
                      });
                      if (res.ok) {
                        // Refresh reviews list
                        const reviewsRes = await fetch(`${API_URL}/api/counseling-reviews?limit=50`);
                        if (reviewsRes.ok) {
                          const data = await reviewsRes.json();
                          if (data.reviews && data.reviews.length > 0) {
                            const dbReviews = data.reviews.map(r => ({
                              content: r.content,
                              author: r.author,
                              service: r.service,
                              rating: r.rating || 5,
                            }));
                            setAllTestimonials([...dbReviews, ...defaultTestimonials]);
                          }
                        }
                      }
                    } catch { /* ignore */ }
                    setReviewSubmitting(false);
                    setShowReviewModal(false);
                    setReviewForm({ rating: 5, service: "", content: "" });
                    setShowReviewCompleteModal(true);
                  }}
                  className="w-full py-4 bg-gradient-to-r from-stone-600 to-stone-500 hover:from-stone-700 hover:to-stone-600 text-white font-semibold rounded-xl"
                  data-testid="review-submit-btn"
                >
                  {reviewSubmitting ? "등록 중..." : "후기 등록하기"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Complete Modal */}
      <AnimatePresence>
        {showReviewCompleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowReviewCompleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-sm w-full shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-stone-600 to-stone-400 p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <PenSquare className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">후기가 등록되었습니다</h3>
              </div>
              <div className="p-6 text-center">
                <p className="text-stone-700 font-medium mb-2">감사합니다!</p>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  소중한 후기를 남겨주셔서 감사합니다.<br />
                  더 나은 상담 서비스를 위해 노력하겠습니다.
                </p>
                <Button
                  onClick={() => setShowReviewCompleteModal(false)}
                  className="w-full py-4 bg-gradient-to-r from-stone-600 to-stone-500 hover:from-stone-700 hover:to-stone-600 text-white font-semibold rounded-xl"
                >
                  확인
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Complete Modal */}
      <AnimatePresence>
        {showCompleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowCompleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-sm w-full shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="complete-modal"
            >
              <div className="bg-gradient-to-r from-stone-600 to-stone-400 p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">결제가 완료되었습니다</h3>
              </div>
              <div className="p-6 text-center">
                <p className="text-stone-700 font-medium mb-2">감사합니다!</p>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  24시간 이내에 연락드리겠습니다.<br />
                  상담일자와 시간은 개별적으로 조율합니다.
                </p>
                <Button
                  onClick={() => setShowCompleteModal(false)}
                  className="w-full py-4 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-semibold rounded-xl"
                >
                  확인
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
